/**
 * Select Component - AWLP Design System
 * Form select dropdown with label, validation states, and consistent styling
 */

import { SelectHTMLAttributes, forwardRef } from 'react';
import styled, { css } from 'styled-components';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
  options: SelectOption[];
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  /** Denser text sizing for tight filter rows/toolbars (e.g. queue filters). Height/padding unchanged. */
  compact?: boolean;
}

const Container = styled.div<{ $fullWidth: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};

  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      width: 100%;
    `}
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SelectWrapper = styled.div<{ $hasError: boolean; $disabled: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  min-height: 44px; /* WCAG compliant touch target */
  min-width: 0; /* Ensures flex children shrink below intrinsic content width */
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: ${({ theme }) => theme.transitions.fast};

  ${({ $hasError, theme }) =>
    $hasError &&
    css`
      border-color: ${theme.colors.status.error.text};

      &:focus-within {
        border-color: ${theme.colors.status.error.text};
        box-shadow: ${theme.shadows.ringError};
      }
    `}

  ${({ $disabled, theme }) =>
    $disabled &&
    css`
      background: ${theme.colors.backgrounds.light};
      cursor: not-allowed;
      opacity: 0.6;
    `}

  &:hover:not(:focus-within) {
    border-color: ${({ theme, $hasError }) =>
      $hasError ? theme.colors.status.error.text : theme.colors.borders.medium};
  }

  &:focus-within {
    border-color: ${({ theme, $hasError }) =>
      $hasError ? theme.colors.status.error.text : theme.colors.primary.base};
    box-shadow: ${({ theme, $hasError }) =>
      $hasError ? theme.shadows.ringError : theme.shadows.ring};
  }
`;

const StyledSelect = styled.select<{ $compact?: boolean }>`
  flex: 1;
  min-width: 0;
  max-width: 100%;
  height: 100%;
  padding: 0 ${({ theme }) => theme.spacing[3]};
  padding-right: ${({ theme }) => theme.spacing[8]}; /* Space for arrow */
  border: none;
  background: transparent;
  font-size: ${({ theme, $compact }) =>
    $compact ? theme.typography.fontSize.sm : theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.text.primary};
  outline: none;
  cursor: pointer;
  appearance: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:focus,
  &:focus-visible {
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
  }

  option {
    color: ${({ theme }) => theme.colors.text.primary};
    background: ${({ theme }) => theme.colors.backgrounds.white};
  }
`;

const Arrow = styled.div`
  position: absolute;
  right: ${({ theme }) => theme.spacing[3]};
  pointer-events: none;
  color: ${({ theme }) => theme.colors.text.secondary};

  &::after {
    content: '';
    display: block;
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid currentColor;
  }
`;

const HelperText = styled.span<{ $isError: boolean }>`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme, $isError }) =>
    $isError ? theme.colors.status.error.text : theme.colors.text.secondary};
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};
`;

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      error,
      helperText,
      fullWidth = false,
      disabled = false,
      compact = false,
      options,
      ...props
    },
    ref,
  ) => {
    const hasError = Boolean(error);

    return (
      <Container $fullWidth={fullWidth}>
        {label && <Label>{label}</Label>}
        <SelectWrapper $hasError={hasError} $disabled={disabled}>
          <StyledSelect ref={ref} disabled={disabled} $compact={compact} {...props}>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </StyledSelect>
          <Arrow />
        </SelectWrapper>
        {(error || helperText) && (
          <HelperText $isError={hasError}>{error || helperText}</HelperText>
        )}
      </Container>
    );
  },
);

Select.displayName = 'Select';

export default Select;
